import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { authActions as actions, authSagas, authSelectors, authTypes } from 'app-components/flux/auth';
import { resourceSagaUtils } from 'app-components/flux/core';
import { datasetsActions, datasetsSelectors } from 'app-components/flux/datasets';
// import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { preferencesActions, preferencesSelectors } from 'app-components/flux/preferences';
import { fetchOnce } from 'app-components/hooks/utils';
import { getHostConfig, getLogger, localPreferences, tracker } from 'app-components/utils/core';
import { config } from 'app-components/utils/persist';

import { clearEntitlements } from 'data/entitlements/entitlementsUtils';
import { cancelTokenSource } from 'utils/axiosFactory';
import { checkLocalStorageAccess } from 'utils/core/localStorage';

import { navigate } from 'NavigateSetter';
import { createPersistConfig } from 'createPersistConfig';
import { clearGlobalCaches } from './caches';
import { clearAuthSession, saveAuthSession } from './storage';

const log = getLogger('utils/auth/sagas');

export function* authUpdated(action) {
  const { authSession, reload, location } = action.payload;
  log.log('Auth update...', authSession, reload, location);

  // save the auth session locally so it survives at reload
  yield call(() => saveAuthSession(authSession));

  if (authSession && authSession.qcsId && authSession.datasetId && localPreferences.getKeepLoggedIn()) {
    createPersistConfig(authSession.qcsId, authSession.datasetId);
  }

  if (reload) {
    if (location) {
      log.info('Hard Redirecting to', location);
      // eslint-disable-next-line no-return-assign
      yield call(() => {
        window.location.href = location;
      });
    }
  } else if (location) {
    log.info('Redirecting to', location);
    navigate(location, { replace: true });
  }
}

export function* authUpdatedActionWatcher() {
  yield takeLatest(actions.authUpdated, authUpdated);
}

export function* authLogoutWatcher() {
  yield takeEvery(actions.authLogout, function* authLogout(action) {
    log.info('Auth Logout...', action.payload);
    tracker.track(tracker.events.logout, {
      description: action.payload.reason,
      context: action.meta?.context,
    });

    // cleanup persisted data
    config?.storageInterface?.clear();
    tracker.track(tracker.events.persistPurge, { description: 'logout' });

    // clear axios requests queue
    cancelTokenSource.cancel('Operation cancelled by logout.');

    // clear the auth session locally and global caches
    //
    clearAuthSession();
    clearEntitlements();
    clearGlobalCaches();

    // trigger 'storage' event = logout other tabs
    localPreferences.setSessionStorage(null);
    localPreferences.removeSessionStorage(); // sessionStorage is in the event so remove it from localStorage

    // cancel sagas
    // yield cancel(sagaTasks);

    localPreferences.setLogoutReason(action.payload?.reason);
    const location = action.payload.location || getHostConfig().homepage_url;
    setTimeout(() => {
      window.location.href = location;
    }, 0); // let dispatched actions to run
  });
}

export function* persistAuthSession({ payload: authSession }) {
  log.info('Peristing Refreshed OAuth Token...', authSession);

  // save the auth session locally so it survives a reload
  yield call(() => saveAuthSession(authSession));
}

export function* persistAuthSessionWatcher() {
  yield takeLatest(actions.persistAuthSession, persistAuthSession);
}

export function* authSelectDatasetAutoActionWatcher() {
  yield takeEvery(actions.authSelectDatasetAuto, function* authSelectDatasetAuto(action) {
    const currentDatasetId = yield select(authSelectors.getDatasetId);

    yield call(fetchOnce, datasetsActions.getDatasets, datasetsSelectors, false, true);
    yield resourceSagaUtils.fetchWait(
      datasetsSelectors,
      datasetsActions.getDatasetsSuccess,
      datasetsActions.getDatasetsFailure,
    );

    // wait for corresponding entitlements
    let entitledDatasetsLoadPending = yield select(datasetsSelectors.getEntitledDatasetsLoadPending);
    while (entitledDatasetsLoadPending) {
      yield take(datasetsActions.getDatasetEntitlementsSuccess);
      entitledDatasetsLoadPending = yield select(datasetsSelectors.getEntitledDatasetsLoadPending);
    }
    const datasets = yield select(datasetsSelectors.getEntitledDatasets);
    const currentDataset = datasets.get(currentDatasetId);

    // TODO: replace with prefs v2 once resolved QCS-17004
    yield call(fetchOnce, preferencesActions.getPreferencesUser, preferencesSelectors, false, true);
    yield resourceSagaUtils.fetchWait(
      preferencesSelectors,
      preferencesActions.getPreferencesSuccess,
      preferencesActions.getPreferencesFailure,
    );
    const selectedDatasetId = yield select(preferencesSelectors.getSharedPreferencesByPath, {
      group: 'user',
      path: ['webApp', 'selectedDatasetId'],
    });
    // const selectedDatasetId = yield select(preferencesV2Selectors.getPreferenceValue, 'selectedDatasetId');
    const selectedDataset = datasets.get(selectedDatasetId);

    const dataset = currentDataset || selectedDataset || (datasets.size === 1 ? datasets.first() : undefined);
    const numDatasets = datasets.size;
    let { location, reload } = action.payload || {};
    if (numDatasets === 0) {
      reload = reload ?? true;
      location = location ?? '/get-started';
    }
    if (dataset?.id !== currentDatasetId || reload || (location && location !== window.location.pathname)) {
      // we have to reload page to re-init persistor setup
      const localStorageOK = checkLocalStorageAccess();
      if (localStorageOK) {
        reload = true;
        location = location ?? '/';
      }

      yield put(
        actions.authSelectDataset(
          authTypes.AuthSelectDatasetPayload({
            dataset,
            reload,
            location,
          }),
        ),
      );
    }
  });
}

export default [
  ...authSagas,
  authUpdatedActionWatcher,
  authLogoutWatcher,
  persistAuthSessionWatcher,
  authSelectDatasetAutoActionWatcher,
];
